/**
 * Who holds which profile and direction: what the caller holds, and the
 * administrator's screen for changing it.
 */

import { http, type RequestOptions } from '@/api/http'
import type { AccessUser, Direction, Identity, Me, Person } from '@/domain/access'

interface MeEnvelope {
  me: Me
}

interface DirectionListEnvelope {
  directions: Direction[]
}

interface PeopleEnvelope {
  people: Person[]
}

interface UserListEnvelope {
  users: AccessUser[]
}

interface UserEnvelope {
  user: AccessUser
}

interface RemovedEnvelope {
  removed: boolean
}

export async function getMe(options: RequestOptions = {}): Promise<Me> {
  const { me } = await http.get<MeEnvelope>('/access/me', options)
  return me
}

export async function listDirections(options: RequestOptions = {}): Promise<Direction[]> {
  const { directions } = await http.get<DirectionListEnvelope>('/access/directions', options)
  return directions
}

/** Everyone the builder knows, open to every signed-in person: it is how a report is shared. */
export async function listPeople(options: RequestOptions = {}): Promise<Person[]> {
  const { people } = await http.get<PeopleEnvelope>('/access/people', options)
  return people
}

export async function listUsers(options: RequestOptions = {}): Promise<AccessUser[]> {
  const { users } = await http.get<UserListEnvelope>('/access/users', options)
  return users
}

/** Make these the profiles and directions somebody holds, and no others. */
export async function setAccess(
  sub: string,
  held: {
    profiles: readonly string[]
    directions: readonly string[]
    consultDirections?: readonly string[]
  },
  who: Identity = {},
  options: RequestOptions = {},
): Promise<AccessUser> {
  // A name or email left out is left as it was; only what is given is sent.
  const { user } = await http.put<UserEnvelope>(
    `/access/users/${encodeURIComponent(sub)}`,
    {
      profiles: held.profiles,
      directions: held.directions,
      consultDirections: held.consultDirections ?? [],
      ...who,
    },
    options,
  )
  return user
}

export async function removeUser(sub: string, options: RequestOptions = {}): Promise<boolean> {
  const { removed } = await http.delete<RemovedEnvelope>(
    `/access/users/${encodeURIComponent(sub)}`,
    options,
  )
  return removed
}
